import { useMemo } from "react";

import { useApiClient } from "@/src/hooks/useApiClient";

/** Cinq parties : ce qui tient sur un panneau sans le faire défiler. */
export const UPCOMING_PREVIEW = 5;

/** `total` est un `count(*)` sur le filtre : une ligne suffit à le connaître. */
const COUNT_ONLY = 1;

const HOUR = 3_600_000;

/**
 * Le pouls des événements : combien il y en a, combien acceptent encore des
 * joueurs, et les prochaines parties à se jouer.
 *
 * ⚠️ **Tout part de `GET /moderation/events`**, jamais de `GET /event/{id}` ni
 * de `get-nearby-events` : c'est la seule route qui rende les trois statuts
 * (`close`, les parties jouées, compris) et l'adresse non masquée.
 */
export function useEventPulse() {
  const $api = useApiClient();

  /**
   * La borne basse des « parties à venir », **arrondie à l'heure**.
   *
   * ⚠️ Deux raisons, et la première est un bug évité : la clé de cache
   * d'`openapi-react-query` est `[méthode, chemin, init]`. Un
   * `new Date().toISOString()` recalculé à chaque rendu produirait une clé
   * neuve à chaque rendu, donc une requête par rendu, sans fin.
   *
   * Arrondie **vers le bas** et non vers le haut : à l'arrondi supérieur, une
   * partie qui commence dans vingt minutes disparaîtrait de la liste. Le prix
   * est qu'une partie commencée il y a moins d'une heure y figure encore —
   * c'est le bon sens du terrain, elle n'est pas finie pour autant.
   */
  const from = useMemo(
    () => new Date(Math.floor(Date.now() / HOUR) * HOUR).toISOString(),
    [],
  );

  const all = $api.useQuery("get", "/api/v1/moderation/events", {
    params: { query: { limit: COUNT_ONLY } },
  });

  const open = $api.useQuery("get", "/api/v1/moderation/events", {
    params: { query: { status: "open", limit: COUNT_ONLY } },
  });

  const upcoming = $api.useQuery("get", "/api/v1/moderation/events", {
    params: {
      query: { limit: UPCOMING_PREVIEW, sort: "date", order: "asc", from },
    },
  });

  return {
    events: upcoming.data?.data?.events ?? [],
    /** Tous statuts confondus, `close` compris. */
    total: all.data?.data?.total,
    /** Ceux qui acceptent encore des candidatures. */
    open: open.data?.data?.total,
    upcoming: upcoming.data?.data?.total,
    isLoading: upcoming.isLoading,
    isError: upcoming.isError,
  };
}
